import React from 'react';
    import { motion } from 'framer-motion';
    import { Link } from 'react-router-dom';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import Navigation from '../components/Navigation';
    import CosmicBackground from '../components/CosmicBackground';

    const { FiTool, FiZap, FiCode, FiEdit3, FiArrowRight, FiCheck, FiStar, FiGift } = FiIcons;

    const PromptToolsPage = () => {
      const handleLinkClick = () => {
        window.scrollTo(0, 0);
      };

      const freeTools = [
        { name: 'Content Generator', description: 'AI-powered content creation for social media,blogs,and marketing', features: ['5 generations/day', 'Basic templates', 'Standard quality'], link: '/tools/social-media-creator' },
        { name: 'Landing Page Builder', description: 'Quick landing pages for your offers and lead magnets', features: ['2 pages/month', 'Basic templates', 'Standard hosting'], link: '/tools/landing-page-builder' },
        { name: 'Email Sequence Creator', description: 'Generate email sequences for your marketing campaigns', features: ['1 sequence/week', 'Basic templates', '5 emails max'], link: '/tools/email-sequence-builder' },
      ];

      const premiumTools = [
        { name: 'Advanced Content Suite', description: 'Unlimited AI content generation with premium templates', features: ['Unlimited generations', 'Premium templates', 'Brand voice training', 'Bulk export'], price: '$29/month', link: '/tools' },
        { name: 'Pro Site Builder', description: 'Professional websites and funnels with advanced features', features: ['Unlimited pages', 'Custom domains', 'Advanced analytics', 'A/B testing'], price: '$49/month', link: '/tools' },
        { name: 'Automation Pro', description: 'Complete marketing automation with AI optimization', features: ['Unlimited sequences', 'Smart optimization', 'Advanced tracking', 'Custom integrations'], price: '$79/month', link: '/tools' },
      ];

      const toolCategories = [
        { icon: FiEdit3, name: 'Content Creation', description: 'Blog posts,social media,ad copy,and more', tools: 12 },
        { icon: FiCode, name: 'Website Building', description: 'Landing pages,sales funnels,and complete sites', tools: 8 },
        { icon: FiZap, name: 'Marketing Automation', description: 'Email sequences,chatbots,and workflow automation', tools: 15 },
        { icon: FiTool, name: 'Business Tools', description: 'Productivity,analytics,and optimization tools', tools: 10 },
      ];

      return (
        <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
          <CosmicBackground />
          <Navigation />

          {/* Hero Section */}
          <section className="pt-32 pb-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center space-x-2 bg-cyan-900/30 px-4 py-2 rounded-full border border-cyan-500/30 mb-6">
                  <SafeIcon icon={FiTool} className="text-cyan-400" />
                  <span className="text-sm font-poppins text-cyan-200">AI-Powered Tools</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-orbitron font-bold mb-6 leading-tight">
                  <span className="clarity-gradient">Prompt Tools</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
                  AI-powered generators and builders that eliminate the guesswork. Create professional content,websites,and campaigns in minutes,not hours.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                  <Link to="/tools" onClick={handleLinkClick}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button flex items-center space-x-2"
                    >
                      <span>Try Free Tools</span>
                      <SafeIcon icon={FiArrowRight} />
                    </motion.button>
                  </Link>
                  <div className="text-center">
                    <div className="flex justify-center items-center space-x-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current text-sm" />
                      ))}
                      <span className="ml-2 text-gray-400 text-sm">4.8/5 from 15K+ users</span>
                    </div>
                    <p className="text-gray-400 font-poppins text-sm">45+ AI-powered tools available</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-cyan-900/40 to-blue-900/40 p-6 rounded-2xl border border-cyan-500/30 backdrop-blur-sm max-w-2xl mx-auto">
                  <div className="flex items-center justify-center space-x-3 mb-2">
                    <SafeIcon icon={FiGift} className="text-cyan-400 text-xl" />
                    <span className="text-cyan-300 font-poppins font-semibold">Always Free Tier</span>
                  </div>
                  <p className="text-gray-300 font-poppins text-center text-sm">
                    Start with our free tools. Upgrade only when you need more power and features.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Tool Categories */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Tool <span className="clarity-gradient">Categories</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Everything you need to build,market,and grow your business with AI.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                {toolCategories.map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-2xl border border-gray-700/50 backdrop-blur-sm hover:border-cyan-500/30 transition-all duration-300 text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={category.icon} className="text-white text-2xl" />
                    </div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">{category.name}</h3>
                    <p className="text-gray-300 font-poppins text-sm mb-3">{category.description}</p>
                    <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 px-3 py-1 rounded-full inline-block">
                      <span className="text-cyan-300 font-poppins text-sm">{category.tools} tools</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Free Tools */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  <span className="clarity-gradient">Free Tools</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Start building with our always-free tools. No credit card required.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {freeTools.map((tool, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-green-800/50 to-emerald-800/50 p-8 rounded-2xl border border-green-500/50 backdrop-blur-sm"
                  >
                    <div className="mb-6">
                      <h3 className="text-2xl font-orbitron font-bold text-white mb-2">{tool.name}</h3>
                      <p className="text-gray-300 font-poppins">{tool.description}</p>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {tool.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-300 font-poppins text-sm">
                          <SafeIcon icon={FiCheck} className="text-green-400 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={tool.link} onClick={handleLinkClick}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full py-3 rounded-full font-poppins font-medium bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 text-green-300 hover:from-green-600/40 hover:to-emerald-600/40 transition-all duration-300"
                      >
                        Try Free
                      </motion.button>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Premium Tools */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  <span className="clarity-gradient">Premium Tools</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Unlock the full power of AI with our professional-grade tools.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {premiumTools.map((tool, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -10 }}
                    className="bg-gradient-to-br from-cyan-800/50 to-blue-800/50 p-8 rounded-2xl border border-cyan-500/50 backdrop-blur-sm"
                  >
                    <div className="mb-6">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-2xl font-orbitron font-bold text-white">{tool.name}</h3>
                        <div className="text-xl font-poppins font-bold text-cyan-400">{tool.price}</div>
                      </div>
                      <p className="text-gray-300 font-poppins">{tool.description}</p>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {tool.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-300 font-poppins text-sm">
                          <SafeIcon icon={FiCheck} className="text-cyan-400 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={tool.link} onClick={handleLinkClick}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full py-3 rounded-full font-poppins font-medium bg-gradient-to-r from-cyan-500 to-blue-500 text-white glow-button hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
                      >
                        Upgrade Now
                      </motion.button>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center bg-gradient-to-r from-cyan-900/40 to-blue-900/40 p-12 rounded-3xl border border-cyan-500/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Ready to Build with <span className="clarity-gradient">AI</span>?
                </h2>
                <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
                  Start with our free tools and upgrade as you grow. AI-powered business building made simple.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <Link to="/tools" onClick={handleLinkClick}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button"
                    >
                      Start with Free Tools
                    </motion.button>
                  </Link>
                  <Link to="/tools" onClick={handleLinkClick}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="border-2 border-cyan-500 text-cyan-300 px-8 py-4 rounded-full font-poppins font-semibold text-lg hover:bg-cyan-500/10 transition-colors"
                    >
                      View All Tools
                    </motion.button>
                  </Link>
                </div>
                <p className="text-gray-400 font-poppins text-sm mt-4">
                  No credit card required for free tools • Upgrade anytime
                </p>
              </motion.div>
            </div>
          </section>
        </div>
      );
    };

    export default PromptToolsPage;